import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import {
  HomeownerAudienceFilterOptionsModel,
  HomeownerAudienceQueryModel,
  HomeownerAudienceRowModel,
  HomeownerAudienceSearchModel,
  HomeownerAudienceSortBy
} from '../shared/models/homeowner-audience.model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  exportHomeownerAudience,
  getHomeownerAudience,
  getHomeownerAudienceFilterOptions
} from './homeowner-audience.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_OPTIONS: HomeownerAudienceFilterOptionsModel = {
  cities: [],
  areas: [],
  properties: [],
  unitTypeGroups: [],
  bedrooms: []
};

const SORT_OPTIONS: { value: HomeownerAudienceSortBy; label: string }[] = [
  { value: 'units', label: 'Most units' },
  { value: 'bookings', label: 'Most bookings' },
  { value: 'payouts', label: 'Highest payouts' },
  { value: 'joined', label: 'Newest joined' },
  { value: 'name', label: 'Name (A–Z)' }
];

type StringOption = { value: string; label: string };

const MANAGEMENT_OPTIONS: StringOption[] = [
  { value: 'managed', label: 'Managed by Birdnest' },
  { value: 'unmanaged', label: 'No longer managed' }
];

const LISTING_OPTIONS: StringOption[] = [
  { value: 'listed', label: 'Listed' },
  { value: 'unlisted', label: 'Unlisted' }
];

const BANK_DETAILS_OPTIONS: StringOption[] = [
  { value: 'provided', label: 'Provided' },
  { value: 'missing', label: 'Missing' }
];

const UNITS_OPTIONS: StringOption[] = [
  { value: 'with', label: 'Has units' },
  { value: 'without', label: 'No units yet' }
];

const CONTACT_OPTIONS: StringOption[] = [
  { value: 'email', label: 'Has email' },
  { value: 'phone', label: 'Has phone' },
  { value: 'both', label: 'Has email and phone' }
];

const formatMoney = (value?: number) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

/** Reads a react-select multi value as a plain array of ids. */
const toIds = (value: ValueType<StringOption, true>): string[] =>
  ((value as StringOption[]) || []).map((option) => option.value);

/** Reads a clearable single react-select as its value, or '' when cleared. */
const toId = (value: ValueType<StringOption, false>): string =>
  (value as StringOption | null)?.value || '';

const COLUMNS = [
  'Name',
  'Email',
  'Phone',
  'Joined',
  'Units',
  'Managed',
  'Listed',
  'Rooms',
  'Bedrooms',
  'Bookings',
  'Last guest stay',
  'Total payouts',
  'Last payout',
  'Bank details',
  'Cities',
  'Areas',
  'Projects'
];

const labelClassName = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const inputClassName = 'w-full h-[38px] px-3 rounded border border-gray-300 text-sm';
const cellClassName = 'border-t border-gray-100 px-3 py-3';

const HomeownerAudienceReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<HomeownerAudienceRowModel[]>([]);
  const [count, setCount] = useState(0);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [sortBy, setSortBy] = useState<HomeownerAudienceSortBy>('units');

  const [search, setSearch] = useState('');
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [unitTypeGroupIds, setUnitTypeGroupIds] = useState<string[]>([]);
  const [bedrooms, setBedrooms] = useState<string[]>([]);
  const [managementStatus, setManagementStatus] = useState('');
  const [listingStatus, setListingStatus] = useState('');
  const [bankDetails, setBankDetails] = useState('');
  const [units, setUnits] = useState('');
  const [contact, setContact] = useState('');

  const [joinedFrom, setJoinedFrom] = useState<Date | null>(null);
  const [joinedTo, setJoinedTo] = useState<Date | null>(null);
  const [checkInFrom, setCheckInFrom] = useState<Date | null>(null);
  const [checkInTo, setCheckInTo] = useState<Date | null>(null);
  const [checkOutFrom, setCheckOutFrom] = useState<Date | null>(null);
  const [checkOutTo, setCheckOutTo] = useState<Date | null>(null);
  const [payoutFrom, setPayoutFrom] = useState<Date | null>(null);
  const [payoutTo, setPayoutTo] = useState<Date | null>(null);

  const [options, setOptions] = useState<HomeownerAudienceFilterOptionsModel>(EMPTY_OPTIONS);

  useEffect(() => {
    getHomeownerAudienceFilterOptions().then((response) => {
      if (!isErrorInfo(response)) {
        setOptions(response);
      } else {
        birdToast.error('Filters could not be loaded', 'Homeowner audience');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // City narrows areas, area narrows projects, exactly as on the guest audience.
  const cityOptions = useMemo(
    () => options.cities.map((city) => ({ value: city.id, label: city.name })),
    [options.cities]
  );

  const areasInScope = useMemo(
    () => options.areas.filter((area) => cityIds.length === 0 || cityIds.includes(area.cityId)),
    [cityIds, options.areas]
  );

  const areaOptions = useMemo(
    () => areasInScope.map((area) => ({ value: area.id, label: area.name })),
    [areasInScope]
  );

  const propertyOptions = useMemo(() => {
    // With no city or area chosen, every project is offered — filtering by the full area list
    // instead would quietly hide any project whose areaId is blank.
    if (cityIds.length === 0 && areaIds.length === 0) {
      return options.properties.map((property) => ({
        value: property.id,
        label: property.name
      }));
    }

    const scopedAreaIds = areaIds.length > 0 ? areaIds : areasInScope.map((area) => area.id);

    return options.properties
      .filter((property) => scopedAreaIds.includes(property.areaId))
      .map((property) => ({ value: property.id, label: property.name }));
  }, [areaIds, areasInScope, cityIds, options.properties]);

  const unitTypeGroupOptions = useMemo(
    () => options.unitTypeGroups.map((group) => ({ value: group.id, label: group.name })),
    [options.unitTypeGroups]
  );

  const bedroomOptions = useMemo(
    () =>
      options.bedrooms.map((count) => ({
        value: String(count),
        label: count === 0 ? 'Studio' : `${count} bedroom${count === 1 ? '' : 's'}`
      })),
    [options.bedrooms]
  );

  /**
   * The filters as the API wants them.
   *
   * Both ends of every range are inclusive, so a "to" date is sent as the last instant of that
   * day — sending its midnight would drop everything that happened during the day picked.
   */
  const buildQuery = useCallback((): HomeownerAudienceQueryModel => {
    const startOf = (date: Date) => moment(date).startOf('day').valueOf();
    const endOf = (date: Date) => moment(date).endOf('day').valueOf();

    return {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(cityIds.length > 0 ? { cityIds } : {}),
      ...(areaIds.length > 0 ? { areaIds } : {}),
      ...(propertyIds.length > 0 ? { propertyIds } : {}),
      ...(unitTypeGroupIds.length > 0 ? { unitTypeGroupIds } : {}),
      ...(bedrooms.length > 0 ? { bedrooms: bedrooms.map(Number) } : {}),
      ...(managementStatus
        ? { managementStatus: managementStatus as HomeownerAudienceQueryModel['managementStatus'] }
        : {}),
      ...(listingStatus
        ? { listingStatus: listingStatus as HomeownerAudienceQueryModel['listingStatus'] }
        : {}),
      ...(bankDetails
        ? { bankDetails: bankDetails as HomeownerAudienceQueryModel['bankDetails'] }
        : {}),
      ...(units ? { units: units as HomeownerAudienceQueryModel['units'] } : {}),
      ...(contact ? { contact: contact as HomeownerAudienceQueryModel['contact'] } : {}),
      ...(joinedFrom ? { joinedFrom: startOf(joinedFrom) } : {}),
      ...(joinedTo ? { joinedTo: endOf(joinedTo) } : {}),
      ...(checkInFrom ? { checkInFrom: startOf(checkInFrom) } : {}),
      ...(checkInTo ? { checkInTo: endOf(checkInTo) } : {}),
      ...(checkOutFrom ? { checkOutFrom: startOf(checkOutFrom) } : {}),
      ...(checkOutTo ? { checkOutTo: endOf(checkOutTo) } : {}),
      ...(payoutFrom ? { payoutFrom: startOf(payoutFrom) } : {}),
      ...(payoutTo ? { payoutTo: endOf(payoutTo) } : {})
    };
  }, [
    areaIds,
    bankDetails,
    bedrooms,
    checkInFrom,
    checkInTo,
    checkOutFrom,
    checkOutTo,
    cityIds,
    contact,
    joinedFrom,
    joinedTo,
    listingStatus,
    managementStatus,
    payoutFrom,
    payoutTo,
    propertyIds,
    search,
    unitTypeGroupIds,
    units
  ]);

  const sortDirectionFor = (sort: HomeownerAudienceSortBy) => (sort === 'name' ? 'asc' : 'desc');

  const loadAudience = useCallback(
    (page: number, perPage: number, sort: HomeownerAudienceSortBy) => {
      setLoading(true);
      setError(false);

      const request: HomeownerAudienceSearchModel = {
        offset: page,
        limit: perPage,
        sortBy: sort,
        sortDirection: sortDirectionFor(sort),
        query: buildQuery()
      };

      getHomeownerAudience(request).then((response) => {
        setLoading(false);
        setHasSearched(true);

        if (isErrorInfo(response)) {
          setError(true);
          setRows([]);
          setCount(0);
          return;
        }

        setRows(response.rows);
        setCount(response.count);
      });
    },
    [buildQuery]
  );

  /** Date ranges only make sense one way round; a reversed pair returns nothing, silently. */
  const validateRanges = (): boolean => {
    const pairs: [Date | null, Date | null, string][] = [
      [joinedFrom, joinedTo, 'Joined'],
      [checkInFrom, checkInTo, 'Check-in'],
      [checkOutFrom, checkOutTo, 'Check-out'],
      [payoutFrom, payoutTo, 'Payout']
    ];

    const reversed = pairs.find(
      ([from, to]) => from && to && moment(to).isBefore(moment(from), 'day')
    );

    if (reversed) {
      birdToast.error(`${reversed[2]} "to" date is before its "from" date`, 'Homeowner audience');
      return false;
    }

    return true;
  };

  const handleGenerate = () => {
    if (!validateRanges()) {
      return;
    }
    setOffset(1);
    loadAudience(1, limit, sortBy);
  };

  const handleSortChange = (next: HomeownerAudienceSortBy) => {
    if (next === sortBy) {
      return;
    }
    setSortBy(next);
    setOffset(1);
    if (hasSearched) {
      loadAudience(1, limit, next);
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setCityIds([]);
    setAreaIds([]);
    setPropertyIds([]);
    setUnitTypeGroupIds([]);
    setBedrooms([]);
    setManagementStatus('');
    setListingStatus('');
    setBankDetails('');
    setUnits('');
    setContact('');
    setJoinedFrom(null);
    setJoinedTo(null);
    setCheckInFrom(null);
    setCheckInTo(null);
    setCheckOutFrom(null);
    setCheckOutTo(null);
    setPayoutFrom(null);
    setPayoutTo(null);
  };

  const downloadCSV = (file: Blob | string) => {
    const link = document.createElement('a');
    const csvBlob = file instanceof Blob ? file : new Blob([file], { type: 'text/csv' });
    link.href = window.URL.createObjectURL(csvBlob);
    link.setAttribute('download', `homeowner-audience-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  };

  /** Exports every homeowner the current filters produce, not the visible page. */
  const handleExport = () => {
    if (!validateRanges()) {
      return;
    }

    setExporting(true);
    exportHomeownerAudience({
      sortBy,
      sortDirection: sortDirectionFor(sortBy),
      query: buildQuery()
    }).then((response) => {
      setExporting(false);

      if (isErrorInfo(response)) {
        birdToast.error('Export failed', 'Homeowner audience');
        return;
      }

      downloadCSV(response);
    });
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadAudience(newPage, limit, sortBy);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadAudience(1, itemsPerPage, sortBy);
  };

  const renderMultiSelect = (
    label: string,
    selectOptions: StringOption[],
    selected: string[],
    placeholder: string,
    onChange: (ids: string[]) => void
  ) => (
    <div>
      <label className={labelClassName}>{label}</label>
      <Select
        isMulti
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectMenuPortalStyles}
        options={selectOptions}
        placeholder={placeholder}
        value={selectOptions.filter((option) => selected.includes(option.value))}
        classNamePrefix="react-select"
        onChange={(value: ValueType<StringOption, true>) => onChange(toIds(value))}
      />
    </div>
  );

  const renderSingleSelect = (
    label: string,
    selectOptions: StringOption[],
    selected: string,
    onChange: (id: string) => void
  ) => (
    <div>
      <label className={labelClassName}>{label}</label>
      <Select
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={selectMenuPortalStyles}
        options={selectOptions}
        placeholder="Any"
        value={selectOptions.find((option) => option.value === selected) ?? null}
        classNamePrefix="react-select"
        onChange={(value: ValueType<StringOption, false>) => onChange(toId(value))}
      />
    </div>
  );

  const renderDate = (
    label: string,
    selected: Date | null,
    onChange: (date: Date | null) => void,
    minDate?: Date | null
  ) => (
    <div>
      <label className={labelClassName}>{label}</label>
      <ReactDatePicker
        selected={selected}
        minDate={minDate ?? undefined}
        onChange={(date: Date | null) => onChange(date)}
        placeholderText="Any"
        isClearable
        className={inputClassName}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      {/* Not sticky like the guest audience header: with twice the filters it would cover most of
          a laptop screen while scrolling the table. */}
      <section className="-mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Homeowner Audience
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            One row per homeowner, with the units they own, the bookings on those units and what we
            have paid them. Unit, stay and payout filters keep only homeowners with a matching unit,
            and the figures count those units only. Every date range is{' '}
            <span className="font-gilroy-bold">inclusive</span> of both ends.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <div>
            <label className={labelClassName}>Search</label>
            <input
              type="text"
              value={search}
              placeholder="Name, email or phone"
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && handleGenerate()}
              className={inputClassName}
            />
          </div>

          {renderMultiSelect('City', cityOptions, cityIds, 'All cities', (ids) => {
            setCityIds(ids);
            setAreaIds([]);
            setPropertyIds([]);
          })}

          {renderMultiSelect('Area', areaOptions, areaIds, 'All areas', (ids) => {
            setAreaIds(ids);
            setPropertyIds([]);
          })}

          {renderMultiSelect(
            'Project',
            propertyOptions,
            propertyIds,
            'All projects',
            setPropertyIds
          )}

          {renderMultiSelect(
            'Unit Type Group',
            unitTypeGroupOptions,
            unitTypeGroupIds,
            'All groups',
            setUnitTypeGroupIds
          )}

          {renderMultiSelect('Bedrooms', bedroomOptions, bedrooms, 'Any', setBedrooms)}

          {renderSingleSelect(
            'Management Status',
            MANAGEMENT_OPTIONS,
            managementStatus,
            setManagementStatus
          )}

          {renderSingleSelect('Listing Status', LISTING_OPTIONS, listingStatus, setListingStatus)}

          {renderSingleSelect('Bank Details', BANK_DETAILS_OPTIONS, bankDetails, setBankDetails)}

          {renderSingleSelect('Units', UNITS_OPTIONS, units, setUnits)}

          {renderSingleSelect('Contact', CONTACT_OPTIONS, contact, setContact)}

          <div>
            <label className={labelClassName}>Sort By</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sortBy) ?? null}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: HomeownerAudienceSortBy; label: string }, false>
              ) => handleSortChange(value?.value || 'units')}
            />
          </div>

          {renderDate('Joined From', joinedFrom, setJoinedFrom)}
          {renderDate('Joined To', joinedTo, setJoinedTo, joinedFrom)}
          {renderDate('Guest Check-in From', checkInFrom, setCheckInFrom)}
          {renderDate('Guest Check-in To', checkInTo, setCheckInTo, checkInFrom)}
          {renderDate('Guest Check-out From', checkOutFrom, setCheckOutFrom)}
          {renderDate('Guest Check-out To', checkOutTo, setCheckOutTo, checkOutFrom)}
          {renderDate('Payout From', payoutFrom, setPayoutFrom)}
          {renderDate('Payout To', payoutTo, setPayoutTo, payoutFrom)}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleGenerate}
            type={ButtonType.primary}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Generate
          </Button>
          <Button
            onClick={handleClearFilters}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Clear filters
          </Button>
          <Button
            onClick={handleExport}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            disabled={!hasSearched || exporting}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          {hasSearched && !loading && !error && (
            <span className="text-sm font-gilroy-medium text-neutral-400">
              {count} homeowner{count === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </section>

      <section>
        {loading && <LoadingData />}

        {!loading && error && (
          <div className="flex flex-col items-center py-10">
            <p className="text-base font-gilroy-medium text-neutral-400">
              The report could not be loaded.
            </p>
            <Button
              onClick={() => loadAudience(offset, limit, sortBy)}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && !hasSearched && (
          <p className="py-10 text-center text-base font-gilroy-medium text-neutral-400">
            Choose your filters and hit Generate. With none set, the report returns every homeowner
            with an account, including those with no units yet.
          </p>
        )}

        {!loading && !error && hasSearched && (
          <>
            {rows.length === 0 ? (
              <NotFoundData />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
                <table className="w-full min-w-[1800px] text-left text-sm">
                  <thead>
                    <tr className="bg-creams-50">
                      {COLUMNS.map((column) => (
                        <th
                          key={column}
                          className="whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.landlordId} className="hover:bg-primary-50/40">
                        <td className={`${cellClassName} whitespace-nowrap`}>{row.name || '—'}</td>
                        <td className={`${cellClassName} whitespace-nowrap`}>{row.email || '—'}</td>
                        <td className={`${cellClassName} whitespace-nowrap`}>{row.phone || '—'}</td>
                        <td className={`${cellClassName} whitespace-nowrap`}>
                          {formatDate(row.joinedAt)}
                        </td>
                        <td className={`${cellClassName} text-right font-gilroy-bold`}>
                          {row.unitsCount}
                        </td>
                        <td className={`${cellClassName} text-right`}>{row.managedUnitsCount}</td>
                        <td className={`${cellClassName} text-right`}>{row.listedUnitsCount}</td>
                        <td className={`${cellClassName} text-right`}>{row.roomsCount}</td>
                        <td className={`${cellClassName} whitespace-nowrap`}>
                          {(row.bedrooms || []).join(', ') || '—'}
                        </td>
                        <td className={`${cellClassName} text-right`}>{row.bookingsCount}</td>
                        <td className={`${cellClassName} whitespace-nowrap`}>
                          {formatDate(row.lastCheckInDate)}
                        </td>
                        <td className={`${cellClassName} text-right whitespace-nowrap`}>
                          {formatMoney(row.totalPayouts)}
                        </td>
                        <td className={`${cellClassName} whitespace-nowrap`}>
                          {formatDate(row.lastPayoutDate)}
                        </td>
                        <td className={`${cellClassName} whitespace-nowrap`}>
                          {row.hasBankAccount ? 'Provided' : 'Missing'}
                        </td>
                        <td className={cellClassName}>{(row.cityNames || []).join(', ') || '—'}</td>
                        <td className={cellClassName}>{(row.areaNames || []).join(', ') || '—'}</td>
                        <td className={cellClassName}>
                          {(row.propertyNames || []).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {count > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={count}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default HomeownerAudienceReportPage;
